import { FC, useMemo, ReactNode } from 'react';
import classnames from 'classnames';
import { Table, Checkbox, TableProps } from 'rsuite';
import { getIntersection } from 'utils/SetUtils';
import styles from './checkboxTable.module.scss';

export interface CheckboxTableProps {
  data: Record<string, any>[];
  uniqueKey: string;
  checkedKeys: Set<any>;
  onSelect: (v: any) => void;
  onAllSelect: () => void;
  disabledKeys?: Set<any>;
  children?: ReactNode;
  className?: string;
  tableConfig?: Omit<TableProps, 'data' | 'children' | 'className'>;
}

export const RsCheckboxTable: FC<CheckboxTableProps> = ({
  data,
  uniqueKey,
  checkedKeys,
  onSelect,
  onAllSelect,
  disabledKeys = new Set(),
  children = null,
  className = '',
  tableConfig = {},
}) => {
  const allValidKeys = useMemo(
    () => new Set(data.map((item) => item[uniqueKey])),
    [data, uniqueKey],
  );
  const validCheckedKeys = useMemo(
    () => getIntersection(allValidKeys, checkedKeys),
    [allValidKeys, checkedKeys],
  );
  const validDisabledKeys = useMemo(
    () => getIntersection(allValidKeys, disabledKeys),
    [allValidKeys, disabledKeys],
  );

  return (
    <Table
      data={data}
      className={classnames(styles.CheckboxTable, className)}
      rowKey={uniqueKey}
      {...tableConfig}
    >
      <Table.Column width={40} fixed>
        <Table.HeaderCell className={styles['checkbox-cell']}>
          <Checkbox
            onChange={onAllSelect}
            checked={
              !!validCheckedKeys.size &&
              validCheckedKeys.size === allValidKeys.size
            }
            indeterminate={
              validCheckedKeys.size > 0 &&
              validCheckedKeys.size < allValidKeys.size
            }
            disabled={validDisabledKeys.size === allValidKeys.size}
          />
        </Table.HeaderCell>
        <Table.Cell className={styles['checkbox-cell']}>
          {(item) => (
            <Checkbox
              checked={validCheckedKeys.has(item[uniqueKey])}
              onChange={() => onSelect(item[uniqueKey])}
              disabled={validDisabledKeys.has(item[uniqueKey])}
            />
          )}
        </Table.Cell>
      </Table.Column>
      {children}
    </Table>
  );
};
